import React, { useState, useEffect, useCallback } from "react";
import { Package, CheckCircle, Clock, XCircle, ChevronDown, BarChart2, RefreshCw, Star } from "lucide-react";
import { PICKUP_WINDOWS, FLAVORS } from "../lib/menuData";
import { supabase } from "../supabaseClient";
import { createClient } from "@supabase/supabase-js";

const adminSupabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const STATUS_CONFIG = {
  pending:   { label: "Pending",   color: "bg-amber-100 text-amber-700",   icon: <Clock size={13} /> },
  confirmed: { label: "Confirmed", color: "bg-blue-100 text-blue-700",     icon: <CheckCircle size={13} /> },
  ready:     { label: "Ready",     color: "bg-green-100 text-green-700",   icon: <Package size={13} /> },
  fulfilled: { label: "Fulfilled", color: "bg-gray-100 text-gray-500",     icon: <CheckCircle size={13} /> },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-500",       icon: <XCircle size={13} /> },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${cfg.color}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function StarDisplay({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={14}
          className={s <= rating ? "text-amber-400 fill-amber-400" : "text-gray-300"}
        />
      ))}
    </div>
  );
}

function ReviewRow({ review, onApprove, onReject }) {
  const [updating, setUpdating] = useState(false);
  const date = new Date(review.created_at).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });

  const handle = async (fn) => {
    setUpdating(true);
    await fn();
    setUpdating(false);
  };

  return (
    <div className="card mb-3 px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <p className="font-bold text-sm text-sage-800">{review.name}</p>
            <StarDisplay rating={review.rating} />
            <span className="text-xs text-gray-400">{date}</span>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">"{review.comment}"</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {!review.approved ? (
            <>
              <button
                onClick={() => handle(onApprove)}
                disabled={updating}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 hover:bg-green-200 transition-colors disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => handle(onReject)}
                disabled={updating}
                className="px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-500 hover:bg-red-200 transition-colors disabled:opacity-50"
              >
                Reject
              </button>
            </>
          ) : (
            <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
              ✓ Approved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderRow({ order, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const pickupWindow = PICKUP_WINDOWS.find((w) => w.id === order.pickup_window);
  const date = new Date(order.created_at).toLocaleDateString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    await onStatusChange(order.id, newStatus);
    setUpdating(false);
  };

  return (
    <div className="card mb-3 overflow-visible">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-cream-50/50 transition-colors"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div>
            <p className="font-bold text-sm text-sage-800 font-mono">{order.id.slice(0, 8).toUpperCase()}</p>
            <p className="text-xs text-gray-400">{date}</p>
          </div>
          <div className="hidden sm:block min-w-0">
            <p className="text-sm font-bold text-gray-700 truncate">{order.first_name} {order.last_name}</p>
            <p className="text-xs text-gray-400 truncate">{order.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="font-bold text-sage-700 text-sm">${Number(order.total).toFixed(2)}</span>
          <StatusBadge status={order.status} />
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-cream-200 px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Customer</p>
              <p className="font-bold text-gray-700">{order.first_name} {order.last_name}</p>
              <p className="text-gray-500 text-xs">{order.email}</p>
              <p className="text-gray-500 text-xs">{order.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Pickup Window</p>
              <p className="font-bold text-gray-700">{order.pickup_label ?? pickupWindow?.label ?? order.pickup_window}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Stripe ID</p>
              <p className="text-gray-500 text-xs font-mono break-all">{order.stripe_payment_id ?? "—"}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-2">Items</p>
            <div className="space-y-1">
              {(order.items ?? []).map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span>{item.name} × {item.qty}</span>
                  <span className="font-bold text-sage-700">${(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {order.special_instructions && (
            <div className="bg-cream-50 rounded-lg p-3 text-xs text-gray-600">
              <strong>Notes:</strong> {order.special_instructions}
            </div>
          )}

          <div>
            <p className="text-xs text-gray-400 mb-2">Update Status</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => handleStatusChange(key)}
                  disabled={updating}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all disabled:opacity-50 ${
                    order.status === key
                      ? cfg.color + " border-transparent"
                      : "border-cream-300 text-gray-500 hover:border-sage-300 hover:text-sage-700"
                  }`}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InventoryManager({ inventory, loading, onUpdate }) {
  return (
    <div className="card p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-sage-800">Weekend Inventory</h3>
        <span className="text-xs text-gray-400">Available (Half-Dozen)</span>
      </div>
      {loading ? (
        <div className="text-center py-8 text-gray-400">
          <RefreshCw size={24} className="mx-auto mb-2 opacity-30 animate-spin" />
          <p className="text-sm">Loading inventory...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {FLAVORS.map((flavor) => {
            const val = inventory[flavor.id] ?? 0;
            return (
              <div key={flavor.id} className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-bold text-sage-800">{flavor.name}</p>
                  <p className="text-xs text-gray-400">{val} × 6 = {val * 6} macarons</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => onUpdate(flavor.id, Math.max(0, val - 1))}
                    className="w-7 h-7 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 font-bold">−</button>
                  <span className="w-8 text-center font-bold text-sage-800">{val}</span>
                  <button onClick={() => onUpdate(flavor.id, val + 1)}
                    className="w-7 h-7 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 font-bold">+</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-gray-400 mt-4 bg-cream-50 p-2 rounded-lg">
        Changes here update live inventory instantly for all customers.
      </p>
    </div>
  );
}

const ADMIN_PIN = "062994";

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [inventory, setInventory] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [filterStatus, setFilterStatus] = useState("all");
  const [reviewFilter, setReviewFilter] = useState("pending");

  const fetchInventory = useCallback(async () => {
    setInventoryLoading(true);
    const { data, error } = await supabase
      .from("inventory")
      .select("flavor_id, available");
    if (!error && data) {
      const inv = {};
      data.forEach((row) => { inv[row.flavor_id] = row.available; });
      setInventory(inv);
    }
    setInventoryLoading(false);
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setOrders(data ?? []);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      setLoadError("Failed to load orders. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const { data, error } = await adminSupabase
        .from("reviews")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setReviews(data ?? []);
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) {
      fetchOrders();
      fetchReviews();
      fetchInventory();
    }
  }, [authed, fetchOrders, fetchReviews, fetchInventory]);

  const handlePin = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) { setAuthed(true); setPinError(""); }
    else { setPinError("Incorrect PIN. Try again."); setPin(""); }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
    const { error } = await supabase
      .from("orders")
      .update({ status: newStatus })
      .eq("id", orderId);
    if (error) { console.error("Status update failed:", error); fetchOrders(); }
  };

  const handleApproveReview = async (reviewId) => {
    setReviews((prev) => prev.map((r) => r.id === reviewId ? { ...r, approved: true } : r));
   const { error } = await adminSupabase
      .from("reviews")
      .update({ approved: true })
      .eq("id", reviewId);
    if (error) { console.error("Approve failed:", error); fetchReviews(); }
  };

  const handleRejectReview = async (reviewId) => {
    setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    const { error } = await adminSupabase
      .from("reviews")
      .delete()
      .eq("id", reviewId);
    if (error) { console.error("Reject failed:", error); fetchReviews(); }
  };

  const handleInventoryUpdate = async (flavorId, val) => {
    setInventory((prev) => ({ ...prev, [flavorId]: val }));
    // NOTE: must be update (not upsert). The anon key has RLS permission to
    // UPDATE inventory but not INSERT, so upsert fails with a 401. New flavor
    // rows must be seeded once in Supabase before they can be adjusted here.
    const { error } = await supabase
      .from("inventory")
      .update({ available: val })
      .eq("flavor_id", flavorId);
    if (error) {
      console.error("Inventory update failed:", error);
      fetchInventory();
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-100 px-4">
        <div className="card p-8 w-full max-w-sm text-center">
          <div className="text-4xl mb-4">🍬</div>
          <h1 className="font-display text-2xl text-sage-800 mb-1">Admin Access</h1>
          <p className="text-gray-500 text-sm mb-6">Enter your PIN to continue.</p>
          <form onSubmit={handlePin} className="space-y-3">
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter PIN"
              className="w-full border border-cream-300 rounded-lg px-4 py-2.5 text-center tracking-widest text-lg outline-none focus:ring-2 focus:ring-sage-300"
              maxLength={8}
              autoFocus
            />
            {pinError && <p className="text-xs text-red-500">{pinError}</p>}
            <button type="submit" className="btn-primary w-full">Enter →</button>
          </form>
        </div>
      </div>
    );
  }

  const filteredOrders = filterStatus === "all" ? orders : orders.filter((o) => o.status === filterStatus);
  const filteredReviews = reviewFilter === "pending"
    ? reviews.filter((r) => !r.approved)
    : reviews.filter((r) => r.approved);

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    confirmed: orders.filter((o) => o.status === "confirmed").length,
    revenue: orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + Number(o.total), 0),
  };

  const pendingReviewCount = reviews.filter((r) => !r.approved).length;

  return (
    <div className="min-h-screen bg-cream-50 pt-6 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl text-sage-900">Admin Dashboard</h1>
            <p className="text-gray-500 text-sm">Order Management</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchOrders(); fetchReviews(); }}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-sage-600 transition-colors disabled:opacity-40"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
            <button onClick={() => setAuthed(false)} className="text-xs text-gray-400 hover:text-red-400 transition-colors">
              Sign Out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total Orders", value: stats.total, icon: <Package size={16} /> },
            { label: "Pending", value: stats.pending, icon: <Clock size={16} /> },
            { label: "Confirmed", value: stats.confirmed, icon: <CheckCircle size={16} /> },
            { label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, icon: <BarChart2 size={16} /> },
          ].map(({ label, value, icon }) => (
            <div key={label} className="bg-white rounded-xl border border-cream-200 p-4">
              <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1">{icon}{label}</div>
              <p className="font-display text-2xl text-sage-800">{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            ["orders", "Orders"],
            ["inventory", "Inventory"],
            ["reviews", `Reviews${pendingReviewCount > 0 ? ` (${pendingReviewCount})` : ""}`],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${
                activeTab === key
                  ? "bg-sage-600 text-white"
                  : "border border-cream-300 text-gray-500 hover:border-sage-400"
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Inventory Tab */}
        {activeTab === "inventory" && (
          <InventoryManager inventory={inventory} loading={inventoryLoading} onUpdate={handleInventoryUpdate} />
        )}

        {/* Orders Tab */}
        {activeTab === "orders" && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {["all", ...Object.keys(STATUS_CONFIG)].map((s) => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all capitalize ${
                    filterStatus === s
                      ? "bg-sage-600 text-white border-transparent"
                      : "border-cream-300 text-gray-500 hover:border-sage-300"
                  }`}>
                  {s === "all" ? `All (${orders.length})` : `${STATUS_CONFIG[s].label} (${orders.filter(o => o.status === s).length})`}
                </button>
              ))}
            </div>

            {loadError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-4">
                {loadError}
              </div>
            )}

            {loading ? (
              <div className="text-center py-16 text-gray-400">
                <RefreshCw size={36} className="mx-auto mb-3 opacity-30 animate-spin" />
                <p className="text-sm">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Package size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">{orders.length === 0 ? "No orders yet." : "No orders with this status."}</p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <OrderRow key={order.id} order={order} onStatusChange={handleStatusChange} />
              ))
            )}
          </>
        )}

        {/* Reviews Tab */}
        {activeTab === "reviews" && (
          <>
            <div className="flex gap-2 mb-4">
              {[
                ["pending", `Pending (${reviews.filter(r => !r.approved).length})`],
                ["approved", `Approved (${reviews.filter(r => r.approved).length})`],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setReviewFilter(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                    reviewFilter === key
                      ? "bg-sage-600 text-white border-transparent"
                      : "border-cream-300 text-gray-500 hover:border-sage-300"
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {reviewsLoading ? (
              <div className="text-center py-16 text-gray-400">
                <RefreshCw size={36} className="mx-auto mb-3 opacity-30 animate-spin" />
                <p className="text-sm">Loading reviews...</p>
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Star size={36} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">
                  {reviewFilter === "pending" ? "No pending reviews!" : "No approved reviews yet."}
                </p>
              </div>
            ) : (
              filteredReviews.map((review) => (
                <ReviewRow
                  key={review.id}
                  review={review}
                  onApprove={() => handleApproveReview(review.id)}
                  onReject={() => handleRejectReview(review.id)}
                />
              ))
            )}
          </>
        )}

      </div>
    </div>
  );
}